/**
 * REVIEW QUEUE STORE
 * 
 * Manages AI agent run state for the Review Queue.
 * Each run represents an AI task that produces a PR with evidence.
 */

import { create } from "zustand";
import { useAiChatStore } from "./aiChatStore";
import { useWorldStore } from "./worldStore";
import { useWorkspaceStore } from "./workspaceStore";
import { useWorkflowStore } from "./workflowStore";
import { api } from "@/lib/api";
import { truncateId } from "@/lib/utils";
import type { AiChatSession, AiProvider, SessionState } from "@/types/ai-chat";

export type RunStatus = "pending" | "running" | "blocked" | "needs_review" | "ready" | "merged" | "failed";
export type RiskLevel = "low" | "medium" | "high" | "critical";
export type IssueType = "bug" | "feature" | "refactor" | "hotfix";

export interface CIStatus {
  name: string;
  status: "pending" | "running" | "passed" | "failed" | "skipped";
  duration?: string;
  url?: string;
}

export interface TestResult {
  passed: number;
  failed: number;
  skipped: number;
  total: number;
  coverage?: number;
}

export interface FileChange {
  path: string;
  additions: number;
  deletions: number;
  isProtected?: boolean;
}

export type AIModel =
  | "claude-opus-4.6"
  | "claude-sonnet-4.5"
  | "claude-haiku-4.5"
  | "codex-5.3"
  | "o3"
  | "gpt-4o"
  | "gemini-2.5-pro"
  | "gemini-2.5-flash"
  | "local"
  | "claude-code"
  // Legacy (for existing data)
  | "claude"
  | "gpt-4"
  | "gpt-3.5"
  | "codex"
  | "gemini";

export interface AIStep {
  id: string;
  model: AIModel;
  action: "plan" | "code" | "review" | "revise" | "test" | "debug";
  summary: string;
  reasoning?: string;
  duration: string;
  timestamp: string;
  inputTokens?: number;
  outputTokens?: number;
  revision?: number;
}

export type ChatMessageRole = "user" | "assistant" | "system" | "tool";

export type ActivityType = "info" | "progress" | "success" | "error" | "tool" | "cost";

export interface ActivityMeta {
  activityType: ActivityType;
  nodeId?: string;
  nodeType?: string;
  nodeLabel?: string;
  toolName?: string;
  costUsd?: number;
  title?: string;
  details?: string;
  source?: "worker-progress" | "worker-log" | "agent";
  variant?: "status" | "command" | "output" | "code";
}

export interface ChatMessage {
  id: string;
  role: ChatMessageRole;
  model?: AIModel;
  content: string;
  codeBlocks?: {
    language: string;
    code: string;
    filename?: string;
  }[];
  timestamp: string;
  tokenCount?: number;
  displayType?: "bubble" | "activity";
  activityMeta?: ActivityMeta;
}

export interface ChatThread {
  id: string;
  title: string;
  messages: ChatMessage[];
  model: AIModel;
  status: "active" | "completed";
}

export interface AIActivity {
  totalSteps: number;
  totalRevisions: number;
  modelsUsed: AIModel[];
  totalDuration: string;
  steps: AIStep[];
}

export interface AgentInvolvement {
  name: string;
  role: "primary" | "reviewer" | "cross-check" | "secondary";
  model: AIModel;
}

export interface DecisionSummary {
  issue: string;
  rootCause: string;
  fixStrategy: string;
}

export interface ScopeImpact {
  subsystemsAffected: string[];
  subsystemsNotAffected: string[];
}

export interface Run {
  id: string;
  projectId: number;
  projectName: string;
  title: string;
  description?: string;
  issueType: IssueType;
  status: RunStatus;
  riskLevel: RiskLevel;
  
  // Decision Summary (new)
  decisionSummary?: DecisionSummary;
  
  // Scope & Impact (new)
  scopeImpact?: ScopeImpact;
  
  // Failure Surface (new) - max 2 bullets
  failureSurface?: string[];
  
  // Agents involved (new) - supports multiple
  agents?: AgentInvolvement[];
  
  // Evidence
  prUrl?: string;
  prNumber?: number;
  branch?: string;
  ciStatus: CIStatus[];
  testResults?: TestResult;
  // Colony step records carrying machine-run verification evidence.
  nodeResults?: Array<{ evidence?: { passed: boolean; checks: Array<{ name: string; passed: boolean; command: string; output_tail: string }> } }>;
  filesChanged: FileChange[];
  diffSummary?: string;
  rationale?: string;
  reproduceSteps?: string[];
  previewUrl?: string;
  
  // Protected zone alerts
  protectedFilesTouched: string[];
  riskFlags: string[];
  
  // Timing
  startedAt: string;
  completedAt?: string;
  estimatedDuration?: string;
  
  // Agent info (legacy, kept for compatibility)
  agentId?: string;
  agentName?: string;
  aiActivity?: AIActivity;
  chatThreads?: ChatThread[];

  // Merge conflict detection
  hasMergeConflict?: boolean;
  mergeConflictFiles?: string[];

  // Read/unread and pin state
  isRead?: boolean;
  isPinned?: boolean;
  lastViewedAt?: string;

  // AI session bridge
  sessionIds?: string[];
  activeSessionId?: string | null;

  // Origin of this run (task-backed vs chat-only)
  source?: "task" | "chat";

  // Back-reference to canonical task state (for task-backed runs)
  taskPipelineStage?: string;
  taskWorkflowStatus?: string;

  // Task intake context
  startInput?: Record<string, any>;
  priority?: string;
  complexity?: string;
  impact?: string;
  workflowName?: string;
}

// ── Adapter functions (session → run) ─────────────────────────────

function sessionStateToRunStatus(state: SessionState): RunStatus {
  const map: Record<SessionState, RunStatus> = {
    idle: "running",
    running: "running",
    reviewing: "needs_review",
    completed: "ready",
    error: "blocked",
  };
  return map[state] ?? "running";
}

function deriveRunFieldsFromSession(session: AiChatSession): Partial<Run> {
  return {
    filesChanged: session.fileChanges.map(fc => ({
      path: fc.path,
      additions: fc.change_type === "created" ? 1 : fc.change_type === "modified" ? 1 : 0,
      deletions: fc.change_type === "deleted" ? 1 : 0,
    })),
    status: sessionStateToRunStatus(session.state),
  };
}

function taskIssueTypeToRunIssueType(issueType: string | undefined): IssueType {
  if (issueType === "bug") return "bug";
  if (issueType === "hotfix") return "hotfix";
  if (issueType === "wp") return "refactor";
  return "feature";
}

export function taskToRunStatus(task: {
  pipelineStage?: string;
  workflowStatus?: string;
  status?: string;
  metadata?: Record<string, any>;
}): RunStatus {
  if (task.pipelineStage === "done") {
    const metadata = task.metadata && typeof task.metadata === "object" ? task.metadata : {};
    const explicitlyMerged =
      metadata.merged === true
      || metadata.pr_merged === true
      || metadata.prMerged === true
      || typeof metadata.merged_at === "string"
      || typeof metadata.mergedAt === "string";
    return explicitlyMerged ? "merged" : "ready";
  }
  if (task.pipelineStage === "error" || task.workflowStatus === "failed") return "blocked";
  if (task.workflowStatus === "cancelled") return "blocked";
  if (task.workflowStatus === "queued" || task.workflowStatus === "running") return "running";
  if (task.pipelineStage === "in_progress") return "running";
  if (task.pipelineStage === "ai_review" || task.pipelineStage === "human_review") return "needs_review";
  if (task.workflowStatus === "completed") return "ready";
  if (task.pipelineStage === "backlog" || task.pipelineStage === "planning") return "pending";
  return "pending";
}

function normalizeRiskLevel(value: unknown): RiskLevel {
  const normalized = typeof value === "string" ? value.toLowerCase() : "";
  if (normalized === "critical") return "critical";
  if (normalized === "high") return "high";
  if (normalized === "medium") return "medium";
  return "low";
}

function toStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === "string");
}

function toFileChanges(value: unknown): FileChange[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => {
      if (!item || typeof item !== "object") return null;
      const row = item as Record<string, unknown>;
      const path = typeof row.path === "string"
        ? row.path
        : typeof row.file === "string"
        ? row.file
        : undefined;
      if (!path) return null;
      return {
        path,
        additions: typeof row.additions === "number" ? row.additions : 0,
        deletions: typeof row.deletions === "number" ? row.deletions : 0,
        isProtected: row.isProtected === true || row.is_protected === true,
      } as FileChange;
    })
    .filter((item): item is FileChange => item !== null);
}

function toCiStatus(value: unknown): CIStatus[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => {
      if (!item || typeof item !== "object") return null;
      const row = item as Record<string, unknown>;
      const name = typeof row.name === "string" ? row.name : "";
      const status = typeof row.status === "string" ? row.status : "";
      if (!name || !["pending", "running", "passed", "failed", "skipped"].includes(status)) {
        return null;
      }
      return {
        name,
        status: status as CIStatus["status"],
        duration: typeof row.duration === "string" ? row.duration : undefined,
        url: typeof row.url === "string" ? row.url : undefined,
      } as CIStatus;
    })
    .filter((item): item is CIStatus => item !== null);
}

function toTestResult(value: unknown): TestResult | undefined {
  if (!value || typeof value !== "object") return undefined;
  const row = value as Record<string, unknown>;
  const passed = typeof row.passed === "number" ? row.passed : 0;
  const failed = typeof row.failed === "number" ? row.failed : 0;
  const skipped = typeof row.skipped === "number" ? row.skipped : 0;
  const total = typeof row.total === "number" ? row.total : passed + failed + skipped;
  const coverage = typeof row.coverage === "number" ? row.coverage : undefined;
  return { passed, failed, skipped, total, coverage };
}

/**
 * Extract the colony step records (carrying verification evidence) from either
 * a workflow node_results object ({ colony_steps: [...] }) or a bare array.
 */
function toColonySteps(
  value: unknown,
): Array<{ evidence?: { passed: boolean; checks: Array<{ name: string; passed: boolean; command: string; output_tail: string }> } }> {
  let steps: unknown = value;
  if (value && typeof value === "object" && !Array.isArray(value)) {
    steps = (value as Record<string, unknown>).colony_steps;
  }
  if (!Array.isArray(steps)) return [];
  return steps.filter((s): s is Record<string, any> => !!s && typeof s === "object");
}

function parsePrNumber(prUrl?: string): number | undefined {
  if (!prUrl) return undefined;
  const match = prUrl.match(/\/pull\/(\d+)/);
  if (!match) return undefined;
  const num = Number(match[1]);
  return Number.isFinite(num) ? num : undefined;
}

function buildRunFromTask(
  task: {
    id: string;
    projectId: number;
    title: string;
    description?: string;
    issueType?: string;
    pipelineStage?: string;
    workflowStatus?: string;
    workflowId?: string;
    metadata?: Record<string, any>;
    priority?: string;
    complexity?: string;
    impact?: string;
  },
  projectName: string,
  previous?: Run,
): Run {
  const metadata = task.metadata && typeof task.metadata === "object" ? task.metadata : {};
  const bugfixPrUrl = metadata.bugfix_steps?.pr?.artifacts?.pr_url;
  const prUrl = typeof metadata.pr_url === "string"
    ? metadata.pr_url
    : typeof metadata.prUrl === "string"
    ? metadata.prUrl
    : typeof metadata.pull_request_url === "string"
    ? metadata.pull_request_url
    : typeof metadata.pullRequestUrl === "string"
    ? metadata.pullRequestUrl
    : typeof bugfixPrUrl === "string"
    ? bugfixPrUrl
    : undefined;
  const startedAtCandidate =
    typeof metadata.started_at === "string"
      ? metadata.started_at
      : typeof metadata.startedAt === "string"
      ? metadata.startedAt
      : typeof metadata.workflow_started_at === "string"
      ? metadata.workflow_started_at
      : typeof metadata.workflowStartedAt === "string"
      ? metadata.workflowStartedAt
      : undefined;
  const completedAt =
    typeof metadata.completed_at === "string"
      ? metadata.completed_at
      : typeof metadata.completedAt === "string"
      ? metadata.completedAt
      : undefined;
  const filesFromMetadata = toFileChanges(
    metadata.files_changed ?? metadata.filesChanged ?? metadata.changed_files ?? metadata.changedFiles,
  );
  const ciFromMetadata = toCiStatus(metadata.ci_status ?? metadata.ciStatus ?? metadata.ci_checks ?? metadata.ciChecks);
  const testResults = toTestResult(metadata.test_results ?? metadata.testResults);
  const colonySteps = toColonySteps(
    metadata.node_results ?? metadata.nodeResults ?? metadata.colony_steps ?? metadata.colonySteps,
  );
  const protectedFilesTouched = toStringArray(metadata.protected_files_touched ?? metadata.protectedFilesTouched);
  const riskFlags = toStringArray(metadata.risk_flags ?? metadata.riskFlags);

  // Task intake context
  const startInput = metadata.startInput ?? metadata.start_input ?? undefined;
  const workflowNameFromMeta = typeof metadata.workflow_name === "string"
    ? metadata.workflow_name
    : typeof metadata.workflowName === "string"
    ? metadata.workflowName
    : undefined;

  // Resolve workflow name from store if not in metadata
  let resolvedWorkflowName = workflowNameFromMeta;
  if (!resolvedWorkflowName && task.workflowId) {
    const wf = useWorkflowStore.getState().workflows.find((w) => w.id === task.workflowId);
    if (wf?.name) resolvedWorkflowName = wf.name;
  }

  return {
    id: task.id,
    projectId: task.projectId,
    projectName,
    title: task.title,
    description: task.description,
    issueType: taskIssueTypeToRunIssueType(task.issueType),
    status: taskToRunStatus(task),
    riskLevel: normalizeRiskLevel(metadata.severity ?? metadata.risk_level ?? metadata.riskLevel),
    prUrl,
    prNumber: parsePrNumber(prUrl),
    branch:
      typeof metadata.branch === "string"
        ? metadata.branch
        : typeof metadata.base_branch === "string"
        ? metadata.base_branch
        : undefined,
    ciStatus: ciFromMetadata.length > 0 ? ciFromMetadata : (previous?.ciStatus ?? []),
    testResults: testResults ?? previous?.testResults,
    nodeResults: colonySteps.length > 0 ? colonySteps : previous?.nodeResults,
    filesChanged: filesFromMetadata.length > 0 ? filesFromMetadata : (previous?.filesChanged ?? []),
    rationale:
      typeof metadata.rationale === "string"
        ? metadata.rationale
        : previous?.rationale,
    reproduceSteps: toStringArray(metadata.reproduce_steps ?? metadata.reproduceSteps),
    previewUrl:
      typeof metadata.preview_url === "string"
        ? metadata.preview_url
        : typeof metadata.previewUrl === "string"
        ? metadata.previewUrl
        : previous?.previewUrl,
    protectedFilesTouched:
      protectedFilesTouched.length > 0 ? protectedFilesTouched : (previous?.protectedFilesTouched ?? []),
    riskFlags: riskFlags.length > 0 ? riskFlags : (previous?.riskFlags ?? []),
    startedAt: startedAtCandidate ?? previous?.startedAt ?? new Date().toISOString(),
    completedAt: completedAt ?? previous?.completedAt,
    estimatedDuration:
      typeof metadata.estimated_duration === "string"
        ? metadata.estimated_duration
        : typeof metadata.estimatedDuration === "string"
        ? metadata.estimatedDuration
        : previous?.estimatedDuration,
    hasMergeConflict:
      typeof metadata.has_merge_conflict === "boolean"
        ? metadata.has_merge_conflict
        : typeof metadata.hasMergeConflict === "boolean"
        ? metadata.hasMergeConflict
        : previous?.hasMergeConflict,
    mergeConflictFiles: toStringArray(metadata.merge_conflict_files ?? metadata.mergeConflictFiles),
    isRead: previous?.isRead ?? false,
    isPinned: previous?.isPinned ?? false,
    lastViewedAt: previous?.lastViewedAt,
    chatThreads: previous?.chatThreads,
    sessionIds: previous?.sessionIds,
    activeSessionId: previous?.activeSessionId ?? null,
    source: "task",
    taskPipelineStage: task.pipelineStage,
    taskWorkflowStatus: task.workflowStatus,
    startInput: startInput && typeof startInput === "object" ? startInput : undefined,
    priority: task.priority ?? (typeof metadata.priority === "string" ? metadata.priority : undefined),
    complexity: task.complexity ?? (typeof metadata.complexity === "string" ? metadata.complexity : undefined),
    impact: task.impact ?? (typeof metadata.impact === "string" ? metadata.impact : undefined),
    workflowName: resolvedWorkflowName,
  };
}

function syncRunsFromWorldTasks() {
  const worldState = useWorldStore.getState();
  const projectNameById = new Map<number, string>(
    worldState.projects.map((project) => [Number(project.id), project.name]),
  );

  useReviewStore.setState((state) => {
    const previousById = new Map(state.runs.map((run) => [run.id, run]));
    const taskIds = new Set(worldState.tasks.map((task) => task.id));

    const taskRuns = worldState.tasks.map((task) => {
      const projectName = resolveRunProjectName(
        task.projectId,
        projectNameById.get(Number(task.projectId)),
      );
      return buildRunFromTask(task, projectName, previousById.get(task.id));
    });

    // Keep chat-only runs and task runs that already contain activity history.
    // This prevents transient task list refreshes from wiping AgentView threads.
    const deletedIds = state._deletedTaskIds;
    const nonTaskRuns = state.runs.filter((run) => {
      if (deletedIds.has(run.id)) return false;
      if (taskIds.has(run.id)) return false;
      if (run.source === "chat") return true;
      if (state.selectedRunId === run.id) return true;
      if (run.id.startsWith("run-")) return true;
      if (run.source === "task" && (run.taskWorkflowStatus === "queued" || run.taskWorkflowStatus === "running")) {
        return true;
      }
      return Boolean(run.chatThreads?.some((thread) => thread.messages.length > 0));
    });
    const runs = [...taskRuns, ...nonTaskRuns];
    const selectedRunId = state.selectedRunId && runs.some((run) => run.id === state.selectedRunId)
      ? state.selectedRunId
      : null;

    return { runs, selectedRunId };
  });
}

function resolveRunProjectName(projectId?: number, projectName?: string): string {
  const normalizedName = projectName?.trim();
  const isGenericName = Boolean(normalizedName && /^Project\s+\d+$/i.test(normalizedName));
  const normalizedProjectId =
    typeof projectId === "number" && Number.isFinite(projectId) && projectId > 0
      ? Number(projectId)
      : null;

  if (normalizedProjectId != null) {
    const worldProject = useWorldStore
      .getState()
      .projects.find((project) => Number(project.id) === normalizedProjectId);
    if (worldProject?.name?.trim()) {
      return worldProject.name.trim();
    }
  }

  if (normalizedName && !isGenericName) {
    return normalizedName;
  }

  if (normalizedName) {
    return normalizedName;
  }

  return "General";
}

interface ReviewState {
  runs: Run[];
  selectedRunId: string | null;
  isLoading: boolean;
  error: string | null;
  _deletedTaskIds: Set<string>;
  
  // Actions
  fetchRuns: () => Promise<void>;
  selectRun: (id: string | null) => void;
  mergeRun: (id: string) => Promise<boolean>;
  rejectRun: (id: string, reason?: string) => Promise<boolean>;
  retryRun: (id: string) => Promise<boolean>;
  resumeRun: (id: string) => Promise<boolean>;
  fetchRunLogLines: (id: string) => Promise<string[]>;
  startRun: (id: string) => Promise<boolean>;
  stopRun: (id: string) => Promise<boolean>;
  
  // Read/unread actions
  markAsRead: (id: string) => void;
  markAsUnread: (id: string) => void;
  toggleRead: (id: string) => void;
  
  // Pin actions
  pinRun: (id: string) => void;
  unpinRun: (id: string) => void;
  togglePin: (id: string) => void;
  
  // Rename and delete
  renameRun: (id: string, newTitle: string) => void;
  deleteRun: (id: string) => Promise<boolean>;

  // Create an empty chat run (used by Quick Fix -> Plan/Agent launch)
  createChatRun: (params: {
    mode: "plan" | "agent";
    projectId?: number;
    projectName?: string;
  }) => string;

  // Create a real AI-backed run
  createRun: (provider: AiProvider, cwd: string, prompt: string, projectId?: number, projectName?: string) => Promise<string>;

  // Activity box helpers
  ensureRunThread: (runId: string) => void;
  appendActivity: (runId: string, message: ChatMessage) => void;
  hydrateRunActivityFromWorkflow: (runId: string) => Promise<void>;
}

// ── Shared helpers for workflow run → ChatMessage[] conversion ───────

function parseNodeResults(value: unknown): Record<string, any> {
  if (!value) return {};
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch {
      return {};
    }
  }
  if (typeof value === "object" && !Array.isArray(value)) {
    return value as Record<string, any>;
  }
  return {};
}

function toText(value: unknown, fallback = ""): string {
  if (typeof value === "string" && value.trim().length > 0) return value.trim();
  return fallback;
}

function toIso(value: unknown, fallback: string): string {
  if (typeof value === "string" && value.trim().length > 0) return value;
  return fallback;
}

function clip(value: string, max = 1200): string {
  return value.length > max ? `${value.slice(0, max - 1)}…` : value;
}

function inferOutputLanguage(value: string): string {
  const sample = value.slice(0, 500);
  if (/diff --git|@@\s+-\d+/.test(sample)) return "diff";
  if (/^\s*[{[]/.test(sample)) return "json";
  if (sample.includes("Traceback")) return "text";
  return "text";
}

function resolveHydrationModel(nodeType: string, workflowNodes: any[] | undefined): AIModel {
  if (!workflowNodes) return "claude-code";
  const node = workflowNodes.find((n: any) => n.type === nodeType);
  if (!node?.config) return "claude-code";
  const provider = node.config.provider as string | undefined;
  const model = node.config.model as string | undefined;
  if (!provider) return "claude-code";
  if (provider === "codex") return "codex-5.3";
  if (provider === "claude") {
    if (model?.includes("opus")) return "claude-opus-4.6";
    if (model?.includes("sonnet")) return "claude-sonnet-4.5";
    if (model?.includes("haiku")) return "claude-haiku-4.5";
    return "claude-opus-4.6";
  }
  if (provider === "gemini") {
    if (model?.includes("flash")) return "gemini-2.5-flash";
    return "gemini-2.5-pro";
  }
  return "claude-code";
}

const AI_CHAT_NODES = new Set(["plan", "code", "subtasks", "llm_review"]);

/**
 * Converts a workflow run record (from SQLite or API) into ChatMessage activity items.
 * Caller is responsible for sorting the returned array if mixing with other sources.
 */
function parseWorkflowRunToActivity(
  wf: Record<string, any>,
  runId: string,
): ChatMessage[] {
  const workflowId = String(wf.id ?? `run-${runId}`);
  const workflowStatus = toText(wf.status, "running").toLowerCase();
  const workflowError = toText(wf.error);
  const startedAt = toIso(wf.started_at, new Date().toISOString());
  const completedAt = toIso(wf.completed_at, startedAt);
  const nodeResults = parseNodeResults(wf.node_results);

  // Hoist store lookups once for all nodes
  const task = useWorldStore.getState().tasks.find((t) => t.id === runId);
  const wfDef = task?.workflowId
    ? useWorkflowStore.getState().workflows.find((w) => w.id === task.workflowId)
    : undefined;
  const workflowNodes = wfDef?.nodes;

  const activity: ChatMessage[] = [];
  activity.push({
    id: `wf-${workflowId}-started`,
    role: "system",
    content: `Workflow started (#${truncateId(workflowId)})`,
    timestamp: startedAt,
    displayType: "activity",
    activityMeta: { activityType: "info" },
  });

  for (const [nodeId, nodeResult] of Object.entries(nodeResults)) {
    const row = nodeResult && typeof nodeResult === "object" ? nodeResult as Record<string, any> : {};
    const status = toText(row.status, "unknown").toLowerCase();
    const ts = toIso(row.completed_at ?? row.started_at, startedAt);
    const nodeError = toText(row.error);
    const nodeOutput = toText(row.output);

    if (status === "completed" || status === "ok" || status === "success") {
      if (AI_CHAT_NODES.has(nodeId) && nodeOutput && nodeOutput.length > 80) {
        const nodeModel = resolveHydrationModel(nodeId, workflowNodes);
        activity.push({
          id: `wf-${workflowId}-node-${nodeId}-completed`,
          role: "assistant",
          model: nodeModel,
          content: nodeId === "llm_review" ? nodeOutput : clip(nodeOutput, 6000),
          timestamp: ts,
          displayType: "bubble",
        });
      } else {
        activity.push({
          id: `wf-${workflowId}-node-${nodeId}-completed`,
          role: "system",
          content: `Node ${nodeId} completed`,
          timestamp: ts,
          codeBlocks: nodeOutput
            ? [{
                language: inferOutputLanguage(nodeOutput),
                code: clip(nodeOutput, 6000),
              }]
            : undefined,
          displayType: "activity",
          activityMeta: {
            activityType: "success",
            nodeId,
            title: `Node ${nodeId} completed`,
            details: nodeOutput ? "Output captured from this step." : undefined,
            variant: nodeOutput ? "output" : "status",
          },
        });
      }
      continue;
    }

    if (status === "failed" || status === "error") {
      activity.push({
        id: `wf-${workflowId}-node-${nodeId}-failed`,
        role: "system",
        content: clip(
          nodeError
            ? `Node ${nodeId} failed: ${nodeError}`
            : `Node ${nodeId} failed`,
        ),
        timestamp: ts,
        codeBlocks: nodeOutput
          ? [{
              language: inferOutputLanguage(nodeOutput),
              code: clip(nodeOutput, 6000),
            }]
          : undefined,
        displayType: "activity",
        activityMeta: {
          activityType: "error",
          nodeId,
          title: `Node ${nodeId} failed`,
          details: nodeError ? clip(nodeError, 1200) : undefined,
          variant: nodeOutput ? "output" : "status",
        },
      });
      continue;
    }

    // Skip "running/pending" hydration rows — live worker events already render
    // these in AgentView, and hydrating them creates duplicate entries.
  }

  if (workflowStatus === "failed") {
    activity.push({
      id: `wf-${workflowId}-final-failed`,
      role: "system",
      content: clip(workflowError ? `Workflow failed: ${workflowError}` : "Workflow failed"),
      timestamp: completedAt,
      displayType: "activity",
      activityMeta: { activityType: "error" },
    });
  } else if (workflowStatus === "completed") {
    activity.push({
      id: `wf-${workflowId}-final-completed`,
      role: "system",
      content: "Workflow completed successfully",
      timestamp: completedAt,
      displayType: "activity",
      activityMeta: { activityType: "success" },
    });
  }

  return activity;
}

/**
 * Flatten a run's failure into readable log lines: the run-level error first
 * (if any), then persisted CLI output lines. Blank lines are dropped.
 * Exported for testing; consumed by fetchRunLogLines.
 */
export function buildLogLines(
  errorText: string | null | undefined,
  outputRows: Array<{ line: string }>,
): string[] {
  const lines: string[] = [];
  if (errorText && errorText.trim()) {
    lines.push(errorText.trim());
  }
  for (const row of outputRows) {
    if (typeof row.line === "string" && row.line.trim()) {
      lines.push(row.line);
    }
  }
  return lines;
}

export const useReviewStore = create<ReviewState>()((set, get) => ({
  runs: [],
  selectedRunId: null,
  isLoading: false,
  error: null,
  _deletedTaskIds: new Set<string>(),

  fetchRuns: async () => {
    set({ isLoading: true, error: null });
    try {
      await useWorldStore.getState().fetchWorldData();
      syncRunsFromWorldTasks();
      set({ isLoading: false });
    } catch (error) {
      set({ isLoading: false, error: (error as Error).message });
    }
  },

  selectRun: (id) => {
    set({ selectedRunId: id });
  },

  mergeRun: async (id) => {
    const worldTask = useWorldStore.getState().tasks.find((task) => task.id === id);
    if (worldTask) {
      return useWorldStore.getState().mergeTaskPR(id);
    }

    set(state => ({
      runs: state.runs.map(r =>
        r.id === id ? { ...r, status: "merged" as RunStatus } : r
      ),
    }));
    return true;
  },

  rejectRun: async (id, _reason) => {
    const worldTask = useWorldStore.getState().tasks.find((task) => task.id === id);
    if (worldTask) {
      const world = useWorldStore.getState();
      if (worldTask.workflowStatus === "queued" || worldTask.workflowStatus === "running") {
        return world.stopTask(id);
      }
      return world.moveTaskToStage(id, "backlog");
    }

    set(state => ({
      runs: state.runs.map(r =>
        r.id === id ? { ...r, status: "failed" as RunStatus } : r
      ),
    }));
    return true;
  },

  retryRun: async (id) => {
    const worldTask = useWorldStore.getState().tasks.find((task) => task.id === id);
    if (worldTask) {
      try {
        return await useWorldStore.getState().retryTaskPipeline(id);
      } catch {
        return false;
      }
    }

    set(state => ({
      runs: state.runs.map(r =>
        r.id === id ? { ...r, status: "running" as RunStatus } : r
      ),
    }));
    return true;
  },

  resumeRun: async (id) => {
    const worldTask = useWorldStore.getState().tasks.find((task) => task.id === id);
    if (!worldTask) return false;
    try {
      return await useWorldStore.getState().resumeTaskFromFailedNode(id);
    } catch {
      return false;
    }
  },

  fetchRunLogLines: async (id) => {
    try {
      const Database = (await import("@tauri-apps/plugin-sql")).default;
      const db = await Database.load("sqlite:factory-local.db");

      let runRows: Array<{ error?: string | null }> = [];
      try {
        runRows = await db.select(
          `SELECT error FROM workflow_runs WHERE task_id = ? ORDER BY started_at DESC LIMIT 1`,
          [id],
        );
        if (runRows.length === 0) {
          runRows = await db.select(
            `SELECT error FROM local_workflow_runs WHERE task_id = ? ORDER BY started_at DESC LIMIT 1`,
            [id],
          );
        }
      } catch {
        // Table may not exist yet — skip
      }

      let outputRows: Array<{ line: string }> = [];
      try {
        outputRows = await db.select(
          `SELECT line FROM workflow_output_log WHERE task_id = ? ORDER BY id ASC LIMIT 2000`,
          [id],
        );
      } catch {
        // Table may not exist yet — skip
      }

      return buildLogLines(runRows[0]?.error, outputRows);
    } catch {
      // Tauri SQL not available (web) — best-effort empty
      return [];
    }
  },

  startRun: async (id) => {
    const worldTask = useWorldStore.getState().tasks.find((task) => task.id === id);
    if (!worldTask) return false;
    return useWorldStore.getState().startTask(id);
  },

  stopRun: async (id) => {
    const worldTask = useWorldStore.getState().tasks.find((task) => task.id === id);
    if (!worldTask) return false;
    return useWorldStore.getState().stopTask(id);
  },

  markAsRead: (id) => {
    set(state => ({
      runs: state.runs.map(r =>
        r.id === id ? { ...r, isRead: true, lastViewedAt: new Date().toISOString() } : r
      ),
    }));
  },

  markAsUnread: (id) => {
    set(state => ({
      runs: state.runs.map(r =>
        r.id === id ? { ...r, isRead: false } : r
      ),
    }));
  },

  toggleRead: (id) => {
    set(state => ({
      runs: state.runs.map(r =>
        r.id === id ? { ...r, isRead: !r.isRead, lastViewedAt: !r.isRead ? new Date().toISOString() : r.lastViewedAt } : r
      ),
    }));
  },

  pinRun: (id) => {
    set(state => ({
      runs: state.runs.map(r =>
        r.id === id ? { ...r, isPinned: true } : r
      ),
    }));
  },

  unpinRun: (id) => {
    set(state => ({
      runs: state.runs.map(r =>
        r.id === id ? { ...r, isPinned: false } : r
      ),
    }));
  },

  togglePin: (id) => {
    set(state => ({
      runs: state.runs.map(r =>
        r.id === id ? { ...r, isPinned: !r.isPinned } : r
      ),
    }));
  },

  renameRun: (id, newTitle) => {
    const worldTask = useWorldStore.getState().tasks.find((task) => task.id === id);
    if (worldTask) {
      void useWorldStore.getState().updateTask(id, { title: newTitle });
    }

    set(state => ({
      runs: state.runs.map(r =>
        r.id === id ? { ...r, title: newTitle } : r
      ),
    }));
  },

  deleteRun: async (id) => {
    const worldTask = useWorldStore.getState().tasks.find((task) => task.id === id);
    if (worldTask) {
      const removed = await useWorldStore.getState().removeTask(id);
      if (!removed) return false;
    }

    set(state => {
      const nextDeleted = new Set(state._deletedTaskIds);
      nextDeleted.add(id);
      return {
        runs: state.runs.filter(r => r.id !== id),
        selectedRunId: state.selectedRunId === id ? null : state.selectedRunId,
        _deletedTaskIds: nextDeleted,
      };
    });
    return true;
  },

  createChatRun: ({ mode, projectId, projectName }) => {
    const now = new Date().toISOString();
    const runId = `run-${typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID() : Date.now()}`;
    const resolvedProjectId = projectId ?? 0;
    const resolvedProjectName = resolveRunProjectName(resolvedProjectId, projectName);
    const title = mode === "plan" ? "Plan Chat" : "Agent Chat";

    const newRun: Run = {
      id: runId,
      projectId: resolvedProjectId,
      projectName: resolvedProjectName,
      title,
      description: mode === "plan" ? "Planning conversation" : "General agent conversation",
      issueType: mode === "plan" ? "refactor" : "feature",
      status: "running",
      riskLevel: "low",
      isRead: false,
      isPinned: false,
      ciStatus: [],
      filesChanged: [],
      protectedFilesTouched: [],
      riskFlags: [],
      startedAt: now,
      chatThreads: [
        {
          id: `${runId}-thread-1`,
          title,
          model: "claude-code",
          status: "active",
          messages: [],
        },
      ],
      source: "chat",
    };

    set((state) => ({
      runs: [newRun, ...state.runs],
      selectedRunId: runId,
    }));

    return runId;
  },

  createRun: async (provider, cwd, prompt, projectId, projectName) => {
    const now = new Date().toISOString();
    const runId = `run-${crypto.randomUUID()}`;
    const resolvedProjectId = projectId ?? 0;
    const resolvedProjectName = resolveRunProjectName(resolvedProjectId, projectName);
    const newRun: Run = {
      id: runId,
      projectId: resolvedProjectId,
      projectName: resolvedProjectName,
      title: prompt.slice(0, 60),
      description: prompt,
      issueType: "feature",
      status: "running",
      riskLevel: "low",
      isRead: false,
      isPinned: false,
      ciStatus: [],
      filesChanged: [],
      protectedFilesTouched: [],
      riskFlags: [],
      startedAt: now,
      sessionIds: [],
      activeSessionId: null,
      source: "chat",
    };
    set(state => ({ runs: [newRun, ...state.runs], selectedRunId: runId }));

    try {
      const sessionId = await useAiChatStore.getState().createSession(provider, cwd, prompt);
      set(state => ({
        runs: state.runs.map(r => r.id === runId
          ? { ...r, sessionIds: [sessionId], activeSessionId: sessionId }
          : r
        ),
      }));
    } catch {
      set(state => ({
        runs: state.runs.map(r => r.id === runId
          ? { ...r, status: "failed" as RunStatus }
          : r
        ),
      }));
    }
    return runId;
  },

  ensureRunThread: (runId) => {
    set((state) => {
      const run = state.runs.find((r) => r.id === runId);
      if (!run) {
        const world = useWorldStore.getState();
        const worldTask = world.tasks.find((task) => task.id === runId);
        if (!worldTask) return state;
        const projectName = resolveRunProjectName(
          worldTask.projectId,
          world.projects.find((project) => Number(project.id) === Number(worldTask.projectId))?.name,
        );
        const runFromTask = buildRunFromTask(worldTask, projectName, undefined);
        return {
          runs: [
            {
              ...runFromTask,
              chatThreads: [
                {
                  id: `${runId}-activity`,
                  title: "Activity",
                  model: "claude-code" as AIModel,
                  status: "active" as const,
                  messages: [],
                },
              ],
            },
            ...state.runs,
          ],
        };
      }
      if (run.chatThreads && run.chatThreads.length > 0) return state;
      return {
        runs: state.runs.map((r) =>
          r.id === runId
            ? {
                ...r,
                chatThreads: [
                  {
                    id: `${runId}-activity`,
                    title: "Activity",
                    model: "claude-code" as AIModel,
                    status: "active" as const,
                    messages: [],
                  },
                ],
              }
            : r
        ),
      };
    });
  },

  appendActivity: (runId, message) => {
    set((state) => {
      const run = state.runs.find((r) => r.id === runId);
      if (!run || !run.chatThreads || run.chatThreads.length === 0) return state;

      const thread = run.chatThreads[0];
      if (thread.messages.some((msg) => msg.id === message.id)) {
        return state;
      }
      const lastMsg = thread.messages[thread.messages.length - 1];

      // Dedup: skip if last message has identical content+role within 2s
      if (lastMsg) {
        const timeDiff = new Date(message.timestamp).getTime() - new Date(lastMsg.timestamp).getTime();
        if (lastMsg.content === message.content && lastMsg.role === message.role && timeDiff < 2000) {
          return state;
        }
      }

      // Cap at 500 messages
      const existingMessages = thread.messages.length >= 500
        ? thread.messages.slice(-499)
        : thread.messages;

      const updatedThread: ChatThread = {
        ...thread,
        messages: [...existingMessages, message],
      };

      return {
        runs: state.runs.map((r) =>
          r.id === runId
            ? { ...r, chatThreads: [updatedThread, ...(r.chatThreads?.slice(1) ?? [])] }
            : r
        ),
      };
    });
  },

  hydrateRunActivityFromWorkflow: async (runId) => {
    const run = get().runs.find((r) => r.id === runId);
    if (!run || run.source === "chat") return;

    try {
      const Database = (await import("@tauri-apps/plugin-sql")).default;
      const db = await Database.load("sqlite:factory-local.db");

      const readLatestRun = async (tableName: string): Promise<any[]> => {
        try {
          return await db.select(
            `SELECT id, status, current_node_id, node_results, started_at, completed_at, error
             FROM ${tableName}
             WHERE task_id = ?
             ORDER BY started_at DESC
             LIMIT 1`,
            [runId],
          );
        } catch {
          return [];
        }
      };

      let rows = await readLatestRun("workflow_runs");
      if (rows.length === 0) {
        rows = await readLatestRun("local_workflow_runs");
      }

      const wf = rows[0];
      if (!wf) return;

      const startedAt = toIso(wf.started_at, new Date().toISOString());
      const activity = parseWorkflowRunToActivity(wf, runId);

      // Hydrate persisted CLI output lines from workflow_output_log
      try {
        const outputRows: { id: number; node_id: string; timestamp: string; line: string }[] =
          await db.select(
            `SELECT id, node_id, timestamp, line FROM workflow_output_log
             WHERE task_id = ? ORDER BY id ASC LIMIT 2000`,
            [runId],
          );
        for (const row of outputRows) {
          if (AI_CHAT_NODES.has(row.node_id)) continue;

          if (row.line.startsWith("[assistant]")) {
            const chatText = row.line.slice("[assistant]".length).trim();
            if (chatText) {
              activity.push({
                id: `wol-${row.id}`,
                role: "assistant",
                model: "claude-code",
                content: clip(chatText, 1200),
                timestamp: row.timestamp || startedAt,
                displayType: "bubble",
              });
            }
            continue;
          }

          if (/^\[result:(success|error|fail)\]/i.test(row.line)) {
            const chatText = row.line.replace(/^\[result:\w+\]\s*/i, "").trim();
            if (chatText) {
              activity.push({
                id: `wol-${row.id}`,
                role: "assistant",
                model: "claude-code",
                content: clip(chatText, 6000),
                timestamp: row.timestamp || startedAt,
                displayType: "bubble",
              });
            }
            continue;
          }

          const trimmedLine = row.line.trim();
          if (/^(event|data|id|retry)\s*:?\s*$/i.test(trimmedLine)) continue;

          const cleanLine = row.line.replace(/^\[exec\]\s*/i, "");
          if (!cleanLine.trim()) continue;

          activity.push({
            id: `wol-${row.id}`,
            role: "system",
            content: clip(cleanLine, 1200),
            timestamp: row.timestamp || startedAt,
            displayType: "activity",
            activityMeta: {
              activityType: "info",
              nodeId: row.node_id || undefined,
              variant: "output",
            },
          });
        }
      } catch {
        // Table may not exist yet — skip
      }

      // Re-sort after adding output log lines
      activity.sort((a, b) => {
        if (a.timestamp && b.timestamp) {
          return a.timestamp < b.timestamp ? -1 : a.timestamp > b.timestamp ? 1 : 0;
        }
        return 0;
      });

      get().ensureRunThread(runId);
      for (const msg of activity) {
        get().appendActivity(runId, msg);
      }
    } catch {
      // Tauri not available (web) — try API fallback
      try {
        const data = await api.get<{ workflow_run?: Record<string, any> }>(`/api/tasks/${runId}/logs`);
        if (data?.workflow_run) {
          const activity = parseWorkflowRunToActivity(data.workflow_run, runId);
          get().ensureRunThread(runId);
          for (const msg of activity) {
            get().appendActivity(runId, msg);
          }
        }
      } catch { /* truly best-effort */ }
    }
  },
}));

// ── Cross-store subscriber: sync session state → run status ─────────

useAiChatStore.subscribe((state, prev) => {
  const { runs } = useReviewStore.getState();
  let changed = false;
  const updatedRuns = runs.map(run => {
    if (!run.activeSessionId) return run;
    const session = state.sessions.get(run.activeSessionId);
    const prevSession = prev.sessions.get(run.activeSessionId);
    if (!session || session === prevSession) return run;
    changed = true;
    return { ...run, ...deriveRunFieldsFromSession(session) };
  });
  if (changed) {
    useReviewStore.setState({ runs: updatedRuns });
  }
});

// Sync review runs from the canonical task source (World Store).
useWorldStore.subscribe((state, prev) => {
  if (state.tasks !== prev.tasks || state.projects !== prev.projects) {
    syncRunsFromWorldTasks();
    // Flush any buffered worker events for tasks that now exist
    import("@/hooks/useWorkerEvents").then((mod) => mod.flushBufferedEvents?.()).catch(() => {});
  }
});

// Clear runs when the active workspace changes so stale projects don't leak across workspaces.
useWorkspaceStore.subscribe((state, prev) => {
  if (state.activeWorkspaceId && state.activeWorkspaceId !== prev.activeWorkspaceId) {
    useReviewStore.setState({ runs: [], selectedRunId: null });
  }
});
